import { defineStore } from "pinia"

/**
 * Site Store - Caches site information to reduce database load
 *
 * Issue #1411: The sites table was getting hammered with repeated lookups.
 * This store caches site data for 1 hour to dramatically reduce API calls.
 *
 * Cache is persisted to localStorage, so it survives page reloads.
 * To clear cache manually: useSiteStore().clearCache()
 */
export const useSiteStore = defineStore(
  "site",
  () => {
    const siteData = ref(null)
    const lastFetch = ref(0)

    const fetchSite = async () => {
      // Cache for 1 hour (3600000 ms)
      // Site info rarely changes, so this is safe
      const CACHE_DURATION = 3600000

      // Return cached data if still valid
      if (Date.now() - lastFetch.value < CACHE_DURATION && siteData.value) {
        return siteData.value
      }

      // Fetch fresh data
      try {
        const { $api } = useNuxtApp()
        const data = await $api("/api/sites")
        siteData.value = data
        lastFetch.value = Date.now()
        return data
      } catch (error) {
        console.error("Error fetching site data:", error)
        // Return cached data if fetch fails
        if (siteData.value) {
          return siteData.value
        }
        throw error
      }
    }

    const clearCache = () => {
      siteData.value = null
      lastFetch.value = 0
    }

    return {
      siteData,
      lastFetch,
      fetchSite,
      clearCache,
    }
  },
  {
    persist: true,
  },
)
